import { useCallback } from "react";
import { InstrumentRedirectInput } from "@helpers";
import { useInstrumentUrl } from "./useInstrumentUrl";
import { useMandatorFeature } from "./useMandatorFeature";

export const buildInvestmentPagePath = (
  investmentId: number,
  depotId?: number
) =>
  depotId
    ? `/depots/${depotId}/investments/${investmentId}`
    : `/investments/${investmentId}`;

type LinkableInvestment = InstrumentRedirectInput & { id?: number };

/**
 * Where an investment name should link to.
 *
 * Mandators with the in-app investment detail page get a link for *every*
 * investment, because that page always exists. Everyone else falls back to the
 * mandator's external instrument page, which `useInstrumentUrl` only resolves
 * for some instruments (valid ISIN, live quote, supported type) — hence names
 * rendering as plain text for the rest.
 *
 * @param depotId set when the list is scoped to a single depot, so the target
 * page keeps that depot in its breadcrumbs.
 */
export const useInvestmentLink = (depotId?: number) => {
  const getInstrumentUrl = useInstrumentUrl();
  const hasInvestmentDetailPage = useMandatorFeature("investmentDetailPage");

  return useCallback(
    (investment: LinkableInvestment): string | undefined => {
      if (hasInvestmentDetailPage && investment.id !== undefined) {
        return buildInvestmentPagePath(investment.id, depotId);
      }

      return getInstrumentUrl(investment);
    },
    [hasInvestmentDetailPage, depotId, getInstrumentUrl]
  );
};
